/* ============================================================================
   OAZAR — Module IA du customizer
   ----------------------------------------------------------------------------
   Ajoute la génération de designs par IA au customizer existant, sans modifier
   customizer-app.js. Le module se greffe sur les fonctions globales déjà
   exposées par l'app (setDrawerMode, addLibraryDesignToCurrentViewAndUpload,
   OazarSmartTemplates, showNotification).

   Trois points d'entrée :
     1. Un tiroir "IA" (carte d'action desktop + bouton mobile)
     2. Un bouton "Générer avec l'IA" dans chaque emplacement image d'un
        smart template (le t-shirt tattoo pré-personnalisé)
     3. Les paramètres d'URL ?ai_prompt= et ?template=

   Chargement : <script src="{{ 'customizer-ai.js' | asset_url }}" defer></script>
   À placer APRÈS customizer-app.js.
   ========================================================================= */

(function () {
  'use strict';

  var BACKEND_BASE = String(window.OAZAR_BACKEND_BASE || 'https://customizer.oazar-shop.com/v2').replace(/\/+$/, '');

  function backendUrl(path) {
    return BACKEND_BASE + '/' + String(path || '').replace(/^\/+/, '');
  }

  /* Les anciens SVG peuvent encore contenir queue.php ou une URL sans /v2.
     On les fait migrer à l'exécution, sans devoir rééditer chaque produit. */
  function normaliserEndpointV2(endpoint) {
    endpoint = String(endpoint || '').trim().replace(/\/queue\.php(?=\?|$)/, '/generate.php');
    if (!endpoint) return endpoint;
    try {
      var url = new URL(endpoint, window.location.href);
      if (url.hostname === 'customizer.oazar-shop.com') {
        if (/\/(?:v2\/)?ai-family-illustration(?:-source-lock)?\/generate\.php$/i.test(url.pathname)) {
          return url.origin + '/ai-family-illustration/generate.php' + url.search;
        }
        var ai = url.pathname.match(/\/(ai-[a-z0-9-]+)\/(?:generate|queue)\.php$/i);
        if (ai) return backendUrl(ai[1] + '/generate.php') + url.search;
        if (/\/ai_generate\.php$/i.test(url.pathname)) return backendUrl('ai_generate.php') + url.search;
      }
      return url.href;
    } catch (e) {
      return endpoint;
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Configuration                                                          */
  /* ---------------------------------------------------------------------- */

  var DEFAULTS = {
    enabled: true,
    endpoint: backendUrl('ai_generate.php'),
    count: 2,
    maxCount: 2,
    freeQuota: 12,             // générations gratuites par navigateur / jour
    quotaScope: 'day',         // 'day' | 'session' | 'none'
    defaultRatio: '1:1',
    removeBackground: true,    // demander un PNG détouré (indispensable en DTF)
    historyLimit: 24,
    ctaLabel: 'IA',
    title: 'Créer un design avec l\'IA',
    intro: 'Décris ton idée, l\'IA génère un visuel prêt à imprimer.'
  };

  var CONFIG = (function () {
    var cfg = {};
    Object.keys(DEFAULTS).forEach(function (k) { cfg[k] = DEFAULTS[k]; });
    // 1) balise JSON injectée par Liquid
    try {
      var el = document.getElementById('oazar-ai-config');
      if (el) {
        var parsed = JSON.parse(el.textContent || '{}');
        Object.keys(parsed).forEach(function (k) {
          if (parsed[k] !== null && parsed[k] !== '' && parsed[k] !== undefined) cfg[k] = parsed[k];
        });
      }
    } catch (e) {}
    // 2) surcharge globale éventuelle
    try {
      if (window.OAZAR_AI_CONFIG && typeof window.OAZAR_AI_CONFIG === 'object') {
        Object.keys(window.OAZAR_AI_CONFIG).forEach(function (k) {
          cfg[k] = window.OAZAR_AI_CONFIG[k];
        });
      }
    } catch (e) {}
    cfg.count = Math.max(1, Math.min(parseInt(cfg.count, 10) || 4, parseInt(cfg.maxCount, 10) || 4));
    return cfg;
  })();
  CONFIG.endpoint = normaliserEndpointV2(CONFIG.endpoint);

  /* Styles proposés. Chaque style ajoute un suffixe au prompt envoyé au backend. */
  var STYLES = [
    { key: 'aucun', label: 'Libre', hint: '' },
    { key: 'cartoon', label: 'Dessin animé', hint: 'cartoon illustration, playful rounded shapes, expressive character, clean bold outlines, flat colors' },
    { key: 'realiste', label: 'Réaliste', hint: 'realistic illustration, natural materials and lighting, clean isolated subject, print ready' },
    { key: 'dessin', label: 'Illustration', hint: 'hand drawn illustration, crisp lines, expressive simple composition' },
    { key: 'aquarelle', label: 'Aquarelle', hint: 'watercolor illustration, soft pigment bleeds, painterly, white background' },
    { key: 'pixel-art', label: 'Pixels', hint: '8-bit pixel art, precise square pixels, limited palette, clean silhouette' },
    { key: 'vintage', label: 'Rétro', hint: 'vintage retro print, halftone texture, muted 70s palette, distressed' }
  ];

  function styleIcon(key) {
    var drawings = {
      aucun: '<path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8z"/><path d="M19 18l.6 1.4L21 20l-1.4.6L19 22l-.6-1.4L17 20l1.4-.6z"/>',
      cartoon: '<circle cx="12" cy="12" r="9"/><path d="M8 10h.01M16 10h.01M8 15c2.5 2 5.5 2 8 0"/><path d="M5 5l2-3 2 3M15 5l2-3 2 3"/>',
      realiste: '<path d="M3 19l5-7 3 3 4-6 6 10z"/><circle cx="7" cy="6" r="2"/><path d="M2 21h20"/>',
      dessin: '<path d="M4 20l4-.8L20 7l-3-3L5 16zM15 6l3 3M4 4h5M4 7h3"/>',
      aquarelle: '<path d="M12 2c-4 5-7 9-7 13a7 7 0 0 0 14 0c0-4-3-8-7-13z"/><path d="M9 17c1 1 2 1.5 3 1.5"/>',
      'pixel-art': '<path d="M4 4h4v4H4zM8 8h4v4H8zM12 4h4v4h-4zM16 8h4v4h-4zM4 12h4v4H4zM8 16h4v4H8zM12 12h4v4h-4zM16 16h4v4h-4z"/>',
      vintage: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><path d="M12 3v4M12 17v4M3 12h4M17 12h4"/>'
    };
    return '<svg class="oz-ai__style-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (drawings[key] || drawings.aucun) + '</svg>';
  }

  var RATIOS = [
    { key: '1:1',  label: 'Carré',    w: 1024, h: 1024 },
    { key: '3:4',  label: 'Portrait', w: 896,  h: 1152 },
    { key: '4:3',  label: 'Paysage',  w: 1152, h: 896  }
  ];
  var QUICK_IDEAS = ['Chat', 'Fleurs', 'Étoiles', 'Dinosaure', 'Cœur', 'Jeu vidéo', 'Lune', 'Soleil', 'Personnage'];

  /* Consignes ajoutées systématiquement : on veut un visuel imprimable,
     pas une photo de t-shirt. C'est ce qui fait la différence de rendu. */
  var PRINT_GUIDE = 'flat design asset isolated on a plain background, subject centered, ' +
    'full subject visible with margin, sharp clean edges, high contrast, print ready artwork';
  var EMBROIDERY_GUIDE = 'simple embroidery-friendly artwork, limited solid colors, bold shapes, ' +
    'clear outlines, no tiny text or fine gradients, subject centered on a plain background';

  var NEGATIVE = 'mockup, t-shirt, garment, clothing, hanger, person wearing, photograph of product, ' +
    'watermark, signature, text artifacts, blurry, low resolution, cropped, frame, border, drop shadow on background';

  /* ---------------------------------------------------------------------- */
  /* Utilitaires                                                            */
  /* ---------------------------------------------------------------------- */

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function notify(msg, type) {
    if (typeof window.showNotification === 'function') window.showNotification(msg, type || 'info');
    else if (type === 'error') console.warn('[oazar-ai]', msg);
  }

  function styleByKey(key) {
    for (var i = 0; i < STYLES.length; i++) if (STYLES[i].key === key) return STYLES[i];
    return STYLES[0];
  }

  function ratioByKey(key) {
    for (var i = 0; i < RATIOS.length; i++) if (RATIOS[i].key === key) return RATIOS[i];
    return RATIOS[0];
  }

  /* -- Quota local (anti-abus léger, la vraie limite est côté serveur) ---- */

  function quotaKey() {
    if (CONFIG.quotaScope === 'day') {
      var d = new Date();
      return 'oazar_ai_quota_' + d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
    }
    return 'oazar_ai_quota_session';
  }

  function quotaUsed() {
    if (CONFIG.quotaScope === 'none') return 0;
    try { return parseInt(localStorage.getItem(quotaKey()) || '0', 10) || 0; } catch (e) { return 0; }
  }

  function quotaBump() {
    if (CONFIG.quotaScope === 'none') return;
    try { localStorage.setItem(quotaKey(), String(quotaUsed() + 1)); } catch (e) {}
  }

  function quotaLeft() {
    if (CONFIG.quotaScope === 'none') return Infinity;
    return Math.max(0, (parseInt(CONFIG.freeQuota, 10) || 0) - quotaUsed());
  }

  /* -- Historique des générations ---------------------------------------- */

  var HISTORY_KEY = 'oazar_ai_history_v1';

  function historyRead() {
    try {
      var raw = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) { return []; }
  }

  function historyPush(entries) {
    try {
      var list = entries.concat(historyRead());
      var seen = {};
      list = list.filter(function (it) {
        if (!it || !it.url || seen[it.url]) return false;
        seen[it.url] = true;
        return true;
      }).slice(0, CONFIG.historyLimit);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------- */
  /* Appel backend                                                          */
  /* ---------------------------------------------------------------------- */

  /* Normalise les réponses les plus courantes :
       { images: [{url}] }            (format natif recommandé)
       { data: [{url}] }              (OpenAI images)
       { data: [{b64_json}] }         (OpenAI base64)
       { output: ["https://..."] }    (Replicate)
       { artifacts: [{base64}] }      (Stability)
       "https://..."                  (une seule URL en texte)                */
  function normalizeImages(payload) {
    var out = [];

    function push(u) {
      if (!u) return;
      u = String(u).trim();
      if (!u) return;
      if (/^[A-Za-z0-9+/=\s]+$/.test(u) && u.length > 512 && u.indexOf('http') !== 0) {
        u = 'data:image/png;base64,' + u.replace(/\s/g, '');
      }
      if (u.indexOf('//') === 0) u = 'https:' + u;
      out.push({ url: u });
    }

    if (!payload) return out;
    if (typeof payload === 'string') { push(payload); return out; }

    var buckets = [payload.images, payload.data, payload.output, payload.artifacts, payload.results];
    buckets.forEach(function (bucket) {
      if (!bucket) return;
      if (typeof bucket === 'string') { push(bucket); return; }
      if (!Array.isArray(bucket)) bucket = [bucket];
      bucket.forEach(function (it) {
        if (!it) return;
        if (typeof it === 'string') { push(it); return; }
        push(it.url || it.src || it.image_url || it.b64_json || it.base64 || it.image);
      });
    });

    if (!out.length) push(payload.url || payload.src || payload.image);
    return out;
  }

  function lireFluxGeneration(res) {
    if (!res.body || typeof res.body.getReader !== 'function' || !window.TextDecoder) {
      return Promise.reject(new Error('Ce navigateur ne peut pas lire le flux de génération.'));
    }
    var reader = res.body.getReader();
    var decoder = new TextDecoder('utf-8');
    var buffer = '';
    var resultat = null;

    function bloc(texte) {
      texte = String(texte || '').trim();
      if (!texte || texte.charAt(0) === ':') return;
      var type = '';
      var donnees = [];
      texte.split('\n').forEach(function (ligne) {
        if (ligne.indexOf('event:') === 0) type = ligne.slice(6).trim();
        if (ligne.indexOf('data:') === 0) donnees.push(ligne.slice(5).trim());
      });
      if (!donnees.length) return;
      var payload = JSON.parse(donnees.join('\n'));
      if (type === 'error' || payload.success === false) throw new Error(payload.error || 'La génération a échoué.');
      if (type === 'done') resultat = payload;
    }

    function vider() {
      buffer = buffer.replace(/\r\n/g, '\n');
      var pos;
      while ((pos = buffer.indexOf('\n\n')) !== -1) {
        bloc(buffer.slice(0, pos));
        buffer = buffer.slice(pos + 2);
      }
    }

    function suite() {
      return reader.read().then(function (partie) {
        if (partie.done) {
          buffer += decoder.decode();
          vider();
          if (buffer.trim()) bloc(buffer);
          if (!resultat) throw new Error('Le serveur a fermé la connexion avant la fin.');
          return resultat;
        }
        buffer += decoder.decode(partie.value, { stream: true });
        vider();
        return suite();
      });
    }
    return suite();
  }

  function buildPrompt(userPrompt, style) {
    var parts = [String(userPrompt || '').trim()];
    var s = styleByKey(style);
    if (s && s.hint) parts.push(s.hint);
    parts.push((window.currentTechnique || 'impression') === 'broderie' ? EMBROIDERY_GUIDE : PRINT_GUIDE);
    return parts.filter(Boolean).join(', ');
  }

  var inFlight = null;

  function fetchProviderImage(provider, body, controller) {
    var requestBody = Object.assign({}, body, { provider: provider, n: 1, count: 1 });
    return fetch(CONFIG.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream, application/json' },
      body: JSON.stringify(requestBody),
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      var responseType = String(res.headers.get('content-type') || '').toLowerCase();
      if (res.ok && responseType.indexOf('text/event-stream') !== -1) return lireFluxGeneration(res);
      return res.text().then(function (txt) {
        var json = null;
        try { json = JSON.parse(txt); } catch (e) {}
        if (!res.ok || (json && json.ok === false)) {
          throw new Error((json && (json.error || json.message)) || ('Erreur serveur (' + res.status + ')'));
        }
        return json != null ? json : txt;
      });
    }).then(function (payload) {
      var images = normalizeImages(payload);
      if (!images.length) throw new Error('Aucune image renvoyée.');
      images[0].provider = provider;
      return images[0];
    });
  }

  function generate(opts) {
    opts = opts || {};
    var userPrompt = String(opts.prompt || '').trim();
    if (userPrompt.length < 3) return Promise.reject(new Error('Décris ton idée en quelques mots.'));
    if (quotaLeft() <= 0) return Promise.reject(new Error('Tu as atteint la limite de générations gratuites pour aujourd\'hui.'));

    var ratio = ratioByKey(opts.ratio || CONFIG.defaultRatio);
    var body = {
      prompt: buildPrompt(userPrompt, opts.style),
      raw_prompt: userPrompt,
      style: opts.style || 'aucun',
      negative_prompt: NEGATIVE,
      n: 1,
      count: 1,
      width: ratio.w,
      height: ratio.h,
      ratio: ratio.key,
      transparent: opts.removeBackground !== false && CONFIG.removeBackground !== false,
      remove_background: opts.removeBackground !== false && CONFIG.removeBackground !== false,
      product: (window.currentProductHandle || (window.currentProduct && window.currentProduct.handle) || ''),
      technique: (window.currentTechnique || 'impression'),
      source: 'customizer'
    };

    if (inFlight && inFlight.abort) { try { inFlight.abort(); } catch (e) {} }
    var controller = ('AbortController' in window) ? new AbortController() : null;
    inFlight = controller;

    return Promise.allSettled([
      fetchProviderImage('recraft', body, controller),
      fetchProviderImage('openai', body, controller)
    ]).then(function (outcomes) {
      var images = outcomes.filter(function (result) { return result.status === 'fulfilled'; })
        .map(function (result) { return result.value; });
      var errors = outcomes.map(function (result, index) {
        if (result.status !== 'rejected') return '';
        return (index === 0 ? 'Recraft' : 'ChatGPT') + ' : ' + ((result.reason && result.reason.message) || 'erreur inconnue');
      }).filter(Boolean);
      if (!images.length) throw new Error(errors.join(' · ') || 'Les deux générations ont échoué.');
      images.forEach(function (im) { im.prompt = userPrompt; im.style = opts.style || ''; im.at = Date.now(); });
      if (errors.length) images.warning = errors.join(' · ');
      quotaBump();
      historyPush(images);
      return images;
    }).finally(function () { inFlight = null; });
  }

  /* ---------------------------------------------------------------------- */
  /* Retrait du fond dans les SVG                                           */
  /* ---------------------------------------------------------------------- */

  /* Recraft dessine presque toujours une forme pleine page derriere
     l'illustration. En vectoriel il n'y a pas de pixels a effacer : il faut
     supprimer la forme.

     Ce traitement se fait ici, dans le navigateur, et non sur le serveur :
     aucun risque de faire echouer une generation ou de depasser un delai
     PHP. Si quoi que ce soit se passe mal, on garde le SVG d'origine.

     La detection est geometrique et non basee sur la couleur : est
     consideree comme fond toute forme, sans rotation ni mise a l'echelle,
     dont la boite englobante couvre au moins 95 % du canevas. Les courbes
     sont exclues : une forme courbe pleine page fait partie du dessin. */

  // Logique de nettoyage du fond, cote navigateur.
  // Testee ici avec un DOM simule, puis integree telle quelle dans customizer-ai.js.

  function parseTransform(t) {
    t = (t || '').trim();
    if (!t || t.toLowerCase() === 'none') return [0, 0];
    var dx = 0, dy = 0, re = /([a-zA-Z]+)\s*\(([^)]*)\)/g, m;
    while ((m = re.exec(t))) {
      var name = m[1].toLowerCase();
      var nums = (m[2].match(/-?\d*\.?\d+(?:[eE]-?\d+)?/g) || []).map(Number);
      if (name === 'translate') { dx += nums[0] || 0; dy += nums[1] || 0; }
      else if (name === 'scale') { for (var i=0;i<nums.length;i++) if (Math.abs(nums[i]-1)>1e-6) return null; }
      else if (name === 'rotate') { if (!nums.length || Math.abs(nums[0])>1e-6) return null; }
      else if (name === 'matrix') {
        if (nums.length !== 6) return null;
        if (Math.abs(nums[0]-1)>1e-6 || Math.abs(nums[1])>1e-6 || Math.abs(nums[2])>1e-6 || Math.abs(nums[3]-1)>1e-6) return null;
        dx += nums[4]; dy += nums[5];
      } else return null;
    }
    return [dx, dy];
  }

  function pathBBox(d) {
    if (/[CcSsQqTtAa]/.test(d)) return null;
    var tokens = d.match(/[MmLlHhVvZz]|-?\d*\.?\d+/g);
    if (!tokens) return null;
    var x=0,y=0,xs=[],ys=[],cmd='',buf=[];
    function flush() {
      var i;
      if (cmd==='M'||cmd==='L') { for(i=0;i+1<buf.length;i+=2){x=buf[i];y=buf[i+1];xs.push(x);ys.push(y);} }
      else if (cmd==='m'||cmd==='l') { for(i=0;i+1<buf.length;i+=2){x+=buf[i];y+=buf[i+1];xs.push(x);ys.push(y);} }
      else if (cmd==='H') { for(i=0;i<buf.length;i++){x=buf[i];xs.push(x);ys.push(y);} }
      else if (cmd==='h') { for(i=0;i<buf.length;i++){x+=buf[i];xs.push(x);ys.push(y);} }
      else if (cmd==='V') { for(i=0;i<buf.length;i++){y=buf[i];xs.push(x);ys.push(y);} }
      else if (cmd==='v') { for(i=0;i<buf.length;i++){y+=buf[i];xs.push(x);ys.push(y);} }
      buf = [];
    }
    tokens.forEach(function(t){
      if (/^[MmLlHhVvZz]$/.test(t)) { flush(); cmd = t; } else buf.push(parseFloat(t));
    });
    flush();
    if (!xs.length) return null;
    return [Math.min.apply(null,xs), Math.min.apply(null,ys), Math.max.apply(null,xs), Math.max.apply(null,ys)];
  }

  function stripSvgBackground(svgText) {
    var doc;
    try { doc = new DOMParser().parseFromString(svgText, 'image/svg+xml'); } catch (e) { return svgText; }
    var root = doc.documentElement;
    if (!root || root.nodeName === 'parsererror' || doc.querySelector('parsererror')) return svgText;

    var W = 0, H = 0, vb = (root.getAttribute('viewBox') || '').trim();
    if (vb) { var p = vb.split(/[\s,]+/); if (p.length === 4) { W = parseFloat(p[2]); H = parseFloat(p[3]); } }
    if (!(W > 0 && H > 0)) { W = parseFloat(root.getAttribute('width')); H = parseFloat(root.getAttribute('height')); }
    if (!(W > 0 && H > 0)) return svgText;

    function len(v, base) {
      if (!v) return 0;
      v = String(v);
      return v.slice(-1) === '%' ? base * parseFloat(v) / 100 : parseFloat(v);
    }

    function isBackground(el) {
      var shift = parseTransform(el.getAttribute('transform'));
      if (!shift) return false;
      var dx = shift[0], dy = shift[1];
      var tag = el.nodeName.toLowerCase();

      if (tag === 'rect') {
        var w = len(el.getAttribute('width'), W), h = len(el.getAttribute('height'), H);
        if (!(w > 0 && h > 0)) return false;
        var x = parseFloat(el.getAttribute('x') || 0) + dx, y = parseFloat(el.getAttribute('y') || 0) + dy;
        return w >= W*0.95 && h >= H*0.95 && Math.abs(x) <= W*0.05 && Math.abs(y) <= H*0.05;
      }
      if (tag === 'path') {
        var bb = pathBBox((el.getAttribute('d') || '').replace(/\s+/g,' ').trim());
        if (!bb) return false;
        var x0=bb[0]+dx, y0=bb[1]+dy, x1=bb[2]+dx, y1=bb[3]+dy;
        return (x1-x0) >= W*0.95 && (y1-y0) >= H*0.95 && Math.abs(x0) <= W*0.05 && Math.abs(y0) <= H*0.05;
      }
      if (tag === 'polygon') {
        var n = (el.getAttribute('points')||'').match(/-?\d*\.?\d+/g) || [];
        if (n.length < 8) return false;
        var X=[],Y=[];
        for (var i=0;i+1<n.length;i+=2){X.push(+n[i]);Y.push(+n[i+1]);}
        return (Math.max.apply(null,X)-Math.min.apply(null,X)) >= W*0.95
            && (Math.max.apply(null,Y)-Math.min.apply(null,Y)) >= H*0.95;
      }
      return false;
    }

    var SKIP = ['defs','clippath','mask','lineargradient','radialgradient','pattern','style','symbol'];
    var removed = 0;

    (function walk(parent) {
      var children = Array.prototype.slice.call(parent.childNodes);
      for (var i = 0; i < children.length; i++) {
        var node = children[i];
        if (node.nodeType !== 1) continue;
        if (SKIP.indexOf(node.nodeName.toLowerCase()) !== -1) continue;
        if (isBackground(node)) { parent.removeChild(node); removed++; continue; }
        walk(node);
      }
    })(root);

    var style = root.getAttribute('style');
    if (style) {
      var cleaned = style.replace(/background(-color)?\s*:[^;]*;?/gi, '').trim();
      if (cleaned) root.setAttribute('style', cleaned); else root.removeAttribute('style');
    }

    return removed ? new XMLSerializer().serializeToString(root) : svgText;
  }

  function looksLikeSvgUrl(url) {
    return /\.svg(?:[?#]|$)/i.test(String(url || ''));
  }

  /* Telecharge un SVG, en retire le fond, et renvoie une URL utilisable.
     En cas de pepin, renvoie l'URL d'origine : jamais de blocage. */
  function cleanSvgIfNeeded(url) {
    if (!looksLikeSvgUrl(url)) return Promise.resolve(url);

    return fetch(url, { mode: 'cors' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var cleaned = stripSvgBackground(text);
        if (cleaned === text) return url;
        var blob = new Blob([cleaned], { type: 'image/svg+xml' });
        return URL.createObjectURL(blob);
      })
      .catch(function (e) {
        console.warn('[oazar-ai] fond SVG non retire', e);
        return url;
      });
  }

  /* ---------------------------------------------------------------------- */
  /* Insertion d'une image IA dans le produit                               */
  /* ---------------------------------------------------------------------- */

  function suggestedName(item) {
    var base = String(item && item.prompt ? item.prompt : 'design-ia')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'design-ia';
    return 'ia-' + base + '.png';
  }

  function addToProduct(item) {
    var raw = item && item.url;
    if (!raw) return Promise.resolve(false);
    var name = suggestedName(item);

    return cleanSvgIfNeeded(raw).then(function (src) {
      return placeOnProduct(src, name);
    });
  }

  function placeOnProduct(src, name) {

    if (typeof window.addLibraryDesignToCurrentViewAndUpload === 'function') {
      return Promise.resolve(window.addLibraryDesignToCurrentViewAndUpload({ src: src, name: name, origin: 'ai' }))
        .then(function () { return true; })
        .catch(function (e) {
          console.warn('[oazar-ai] insertion échouée, repli direct', e);
          if (typeof window.addDesignToCurrentView === 'function') {
            window.addDesignToCurrentView(src, 'image', name, 'ai');
            return true;
          }
          return false;
        });
    }
    if (typeof window.addDesignToCurrentView === 'function') {
      window.addDesignToCurrentView(src, 'image', name, 'ai');
      return Promise.resolve(true);
    }
    return Promise.resolve(false);
  }

  /* ---------------------------------------------------------------------- */
  /* Écran IA (tiroir)                                                      */
  /* ---------------------------------------------------------------------- */

  var uiState = {
    prompt: '',
    style: 'aucun',
    ratio: CONFIG.defaultRatio,
    removeBg: CONFIG.removeBackground !== false,
    results: [],
    loading: false,
    error: '',
    warning: '',
    /* Quand non nul : les résultats remplissent un emplacement de template
       au lieu d'être posés librement sur le produit. */
    slotTarget: null
  };

  function screenHtml() {
    var left = quotaLeft();
    var history = historyRead();

    return [
      '<div class="oz-ai">',

      '  <p class="oz-ai__intro">', esc(CONFIG.intro), '</p>',
      uiState.warning ? '<p class="oz-ai__warning">' + esc(uiState.warning) + '</p>' : '',

      '  <label class="oz-ai__field">',
      '    <span>Ton idée</span>',
      '    <textarea class="oz-ai__prompt" rows="3" maxlength="400" placeholder="Ex : un chat dans un style dessin, des fleurs, un texte...">', esc(uiState.prompt), '</textarea>',
      '  </label>',

      '  <div class="oz-ai__field">',
      '    <span>Style</span>',
      '    <div class="oz-ai__chips oz-ai__style-choices">',
      STYLES.map(function (s) {
        return '<button type="button" class="oz-ai__chip' + (s.key === uiState.style ? ' is-active' : '') +
          '" data-ai-style="' + esc(s.key) + '">' + styleIcon(s.key) + '<span>' + esc(s.label) + '</span></button>';
      }).join(''),
      '    </div>',
      '  </div>',

      '  <div class="oz-ai__row">',
      '    <div class="oz-ai__field oz-ai__field--grow">',
      '      <span>Format</span>',
      '      <div class="oz-ai__chips">',
      RATIOS.map(function (r) {
        return '<button type="button" class="oz-ai__chip' + (r.key === uiState.ratio ? ' is-active' : '') +
          '" data-ai-ratio="' + esc(r.key) + '">' + esc(r.label) + '</button>';
      }).join(''),
      '      </div>',
      '    </div>',
      '  </div>',

      '  <div class="oz-ai__field oz-ai__ideas">',
      '    <span>Idées rapides</span>',
      '    <div class="oz-ai__idea-list">', QUICK_IDEAS.map(function(idea) {
        return '<button type="button" class="oz-ai__idea" data-ai-idea="' + esc(idea) + '">' + esc(idea) + '</button>';
      }).join(''), '</div>',
      '  </div>',
      '  <label class="oz-ai__switch">',
      '    <input type="checkbox" class="oz-ai__removebg"', uiState.removeBg ? ' checked' : '', '>',
      '    <span>Détourer le fond (recommandé pour l\'impression)</span>',
      '  </label>',

      '  <button type="button" class="oz-ai__generate"', (uiState.loading || left <= 0) ? ' disabled' : '', '>',
      uiState.loading ? '<span class="oz-ai__spin"></span> Génération en cours…' : 'Générer mon visuel',
      '  </button>',

      '  <p class="oz-ai__quota">3 générations par jour pour cette IA, par connexion. Renouvellement à minuit, heure de Paris.</p>',

      uiState.error ? '<p class="oz-ai__error">' + esc(uiState.error) + '</p>' : '',

      uiState.loading
        ? '<div class="oz-ai__grid">' + new Array(CONFIG.count + 1).join('<div class="oz-ai__skeleton"></div>') + '</div>'
        : '',

      uiState.results.length
        ? '<div class="oz-ai__results">' +
          '<h4>Résultats</h4>' +
          '<div class="oz-ai__grid">' +
          uiState.results.map(function (it, i) {
            return '<figure class="oz-ai__card" data-ai-result="' + i + '" role="button" tabindex="0" aria-label="' + (uiState.slotTarget ? 'Utiliser' : 'Ajouter') + ' le visuel ' + (i + 1) + '">' +
              '<img src="' + esc(it.url) + '" alt="Proposition ' + (i + 1) + '" loading="lazy">' +
              '</figure>';
          }).join('') +
          '</div></div>'
        : '',

      (!uiState.loading && !uiState.results.length && history.length)
        ? '<div class="oz-ai__results oz-ai__results--history">' +
          '<h4>Tes générations précédentes</h4>' +
          '<div class="oz-ai__grid">' +
          history.slice(0, 8).map(function (it, i) {
            return '<figure class="oz-ai__card" data-ai-history="' + i + '" role="button" tabindex="0" aria-label="' + (uiState.slotTarget ? 'Utiliser' : 'Ajouter') + ' le visuel précédent ' + (i + 1) + '">' +
              '<img src="' + esc(it.url) + '" alt="Génération précédente" loading="lazy">' +
              '</figure>';
          }).join('') +
          '</div></div>'
        : '',

      '</div>'
    ].join('');
  }

  function bindScreen(root) {
    if (!root) return;

    var promptEl = root.querySelector('.oz-ai__prompt');
    if (promptEl) {
      promptEl.addEventListener('input', function () { uiState.prompt = promptEl.value; });
      promptEl.addEventListener('keydown', function (e) {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); runGenerate(root); }
      });
    }

    root.querySelectorAll('[data-ai-style]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        uiState.style = btn.getAttribute('data-ai-style');
        root.querySelectorAll('[data-ai-style]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
      });
    });
    root.querySelectorAll('[data-ai-idea]').forEach(function(button) {
      button.addEventListener('click', function() {
        var idea = button.getAttribute('data-ai-idea') || '';
        var prompt = root.querySelector('.oz-ai__prompt');
        if (!prompt || !idea) return;
        prompt.value = prompt.value.trim() ? prompt.value.trim() + ', ' + idea.toLowerCase() : 'Un ' + idea.toLowerCase() + ' en illustration';
        prompt.dispatchEvent(new Event('input', { bubbles: true }));
        prompt.focus();
      });
    });

    root.querySelectorAll('[data-ai-ratio]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        uiState.ratio = btn.getAttribute('data-ai-ratio');
        root.querySelectorAll('[data-ai-ratio]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
      });
    });

    var bg = root.querySelector('.oz-ai__removebg');
    if (bg) bg.addEventListener('change', function () { uiState.removeBg = bg.checked; });

    var gen = root.querySelector('.oz-ai__generate');
    if (gen) gen.addEventListener('click', function () { runGenerate(root); });

    root.querySelectorAll('[data-ai-result]').forEach(function (card) {
      card.addEventListener('click', function () {
        var item = uiState.results[parseInt(card.getAttribute('data-ai-result'), 10)];
        useImage(item);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
      });
    });

    root.querySelectorAll('[data-ai-history]').forEach(function (card) {
      card.addEventListener('click', function () {
        var item = historyRead()[parseInt(card.getAttribute('data-ai-history'), 10)];
        useImage(item);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
      });
    });
  }

  function repaint() {
    var host = document.querySelector('.oz-ai') && document.querySelector('.oz-ai').parentNode;
    if (!host) return;
    host.innerHTML = screenHtml();
    bindScreen(host);
  }

  function renderInto(body) {
    body.innerHTML = screenHtml();
    bindScreen(body);
  }

  function runGenerate(root) {
    var promptEl = root.querySelector('.oz-ai__prompt');
    if (promptEl) uiState.prompt = promptEl.value;

    uiState.loading = true;
    uiState.error = '';
    uiState.warning = '';
    uiState.results = [];
    repaint();

    generate({
      prompt: uiState.prompt,
      style: uiState.style,
      ratio: uiState.ratio,
      count: CONFIG.count,
      removeBackground: uiState.removeBg
    }).then(function (images) {
      uiState.results = images;
      uiState.warning = images.warning || '';
      uiState.loading = false;
      repaint();
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return;
      uiState.loading = false;
      uiState.error = (err && err.message) ? err.message : 'La génération a échoué.';
      repaint();
      notify(uiState.error, 'error');
    });
  }

  function useImage(item) {
    if (!item || !item.url) return;

    if (uiState.slotTarget) {
      fillSlot(uiState.slotTarget, item).then(function (ok) {
        if (ok) {
          notify('Image remplacée dans le modèle', 'success');
          uiState.slotTarget = null;
          closeAiDrawer();
        }
      });
      return;
    }

    addToProduct(item).then(function (ok) {
      if (!ok) { notify('Impossible d\'ajouter ce visuel.', 'error'); return; }
      notify('Design ajouté au produit', 'success');
      closeAiDrawer();
    });
  }

  function closeAiDrawer() {
    if (typeof window.closeDrawer === 'function') { window.closeDrawer(); return; }
    var d = document.getElementById('customizationDrawer');
    if (d) d.setAttribute('aria-hidden', 'true');
  }

  function openAi(options) {
    options = options || {};
    if (options.prompt) uiState.prompt = options.prompt;
    if (options.style) uiState.style = options.style;
    uiState.slotTarget = options.slotTarget || null;
    uiState.error = '';
    uiState.results = [];

    if (typeof window.openDrawer === 'function') window.openDrawer();
    else {
      var d = document.getElementById('customizationDrawer');
      if (d) d.setAttribute('aria-hidden', 'false');
    }

    if (typeof window.setDrawerMode === 'function') {
      window.setDrawerMode('ai');
    } else {
      var body = document.getElementById('drawerBody');
      var title = document.getElementById('drawerTitle');
      if (title) title.textContent = CONFIG.title;
      if (body) renderInto(body);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Boutons d'entrée (desktop + mobile)                                    */
  /* ---------------------------------------------------------------------- */

  var AI_ICON =
    '<svg class="oz-studio-icon" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="m12 2 1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2Zm7 14 .7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7L19 16Z"/>' +
    '</svg>';

  function injectDesktopCard() {
    if (document.getElementById('openAiDrawer')) return;
    var grid = document.querySelector('.quick-actions-grid');
    if (!grid) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'action-card action-card--ai';
    btn.id = 'openAiDrawer';
    btn.setAttribute('data-technique', 'impression broderie');
    btn.innerHTML = '<div class="action-icon">' + (window.oazarProvidedIcon ? window.oazarProvidedIcon('ia') : AI_ICON) + '</div><span>Créer</span><small class="studio-ai-badge">IA</small>';
    btn.addEventListener('click', function () {
      openAi();
    });

    var designs = document.getElementById('openDesignsDrawer');
    if (designs && designs.parentNode === grid) grid.insertBefore(btn, designs);
    else grid.appendChild(btn);
  }

  function injectMobileButton() {
    if (document.getElementById('mobileAiBtn')) return;
    var bar = document.querySelector('.mobile-action-bar-inner');
    if (!bar) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mobile-action-btn mobile-action-btn--ai';
    btn.id = 'mobileAiBtn';
    btn.setAttribute('aria-label', 'Générer un design avec l\'IA');
    btn.innerHTML = (window.oazarProvidedIcon ? window.oazarProvidedIcon('ia') : AI_ICON) + '<span>' + esc(CONFIG.ctaLabel) + '</span>';
    btn.addEventListener('click', function () { openAi(); });

    var designs = document.getElementById('mobileDesignsBtn');
    if (designs && designs.parentNode === bar) bar.insertBefore(btn, designs.nextSibling);
    else bar.appendChild(btn);
  }

  /* ---------------------------------------------------------------------- */
  /* Intégration aux emplacements image des smart templates                 */
  /* ---------------------------------------------------------------------- */

  /* Remplit un <input type="file" data-image-slot="..."> avec une image IA.
     On passe par un vrai File + événement change : le handler existant du
     module smart template s'occupe du reste (state, preview, rerender). */
  function fillSlot(slotKey, item) {
    var input = document.querySelector('[data-image-slot="' + String(slotKey).replace(/"/g, '\\"') + '"]');
    if (!input) return Promise.resolve(false);

    return cleanSvgIfNeeded(item.url)
      .then(function (url) {
        if (/^data:/i.test(String(url || ''))) return dataUriVersBlob(url);
        return fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' }).then(function (r) {
          if (!r.ok) throw new Error('fetch ' + r.status);
          return r.blob();
        });
      })
      .then(function (blob) {
        var file = new File([blob], suggestedName(item), { type: blob.type || 'image/png' });
        var dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
        input.dataset.ozAiResult = '1';
        input.dataset.ozAiResultLocal = item.local ? '1' : '0';
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      })
      .catch(function (e) {
        /* Une URL distante peut marcher dans la miniature HTML mais rester
           invisible dans le SVG autonome du produit. Conserver la photo
           source et signaler l'echec plutot qu'annoncer une fausse reussite. */
        console.warn('[oazar-ai] illustration non integree au produit', e);
        notify('L\'illustration n\'a pas pu etre affichee sur le produit. Reessayez.', 'error');
        return false;
      });
  }

  /* Ajoute un bouton "IA" à côté de chaque "Importer une image" du modal
     smart template. Le modal est reconstruit à chaque rendu, on observe. */
  /* ------------------------------------------------------------------ */
  /* Transformation d'une photo importée                                */
  /* ------------------------------------------------------------------ */

  /* Deux IA cohabitent désormais.

     La première, historique, part d'un texte : on décrit une idée, le
     serveur renvoie des visuels. C'est openAi().

     La seconde part d'une photo déjà importée par le client et la transforme :
     détourage animal ou illustration famille. Chaque modèle vise son endpoint
     dédié, avec un mode et un preset, sans prompt saisi par le client.

     L'atelier ne lit pas le JSON de configuration de la fiche produit. La
     configuration voyage donc dans le SVG, portée par l'emplacement photo
     en attributs data-oz-ai-*, que customizer-app.js recopie sur la carte
     du panneau en data-ai-*. C'est le seul support commun aux deux
     parcours. */

  function lireConfigSlot(actions) {
    var carte = actions.closest ? actions.closest('.oz-smarttpl-image-slot') : null;
    if (!carte) return null;

    var endpoint = carte.getAttribute('data-ai-endpoint');
    if (!endpoint) return null;

    /* Compatibilité avec les SVG créés pendant l'essai de file d'attente :
       queue.php n'est plus utilisé, tout repasse directement par generate.php. */
    endpoint = normaliserEndpointV2(endpoint);

    return {
      endpoint: endpoint,
      mode: carte.getAttribute('data-ai-mode') || '',
      preset: carte.getAttribute('data-ai-preset') || '',
      label: carte.getAttribute('data-ai-label') || "Transformer avec l'IA",
      loading: carte.getAttribute('data-ai-loading') || 'Transformation\u2026',
      error: carte.getAttribute('data-ai-error') || 'La transformation a \u00e9chou\u00e9.',
      timeoutMs: parseInt(carte.getAttribute('data-ai-timeout') || '600000', 10) || 600000
    };
  }

  /* Le panneau est entierement reconstruit par customizer-app.js apres chaque
     import. Le File doit donc vivre ici, hors du DOM : une propriete posee sur
     la carte disparaitrait avec l'ancienne carte. */
  var fichiersPhotoParSlot = Object.create(null);

  function activerMemoirePhoto() {
    document.addEventListener('change', function (e) {
      var input = e && e.target;
      if (!input || !input.getAttribute) return;

      var slotKey = String(input.getAttribute('data-image-slot') || '').trim();
      if (!slotKey) return;
      if (!input.files || !input.files[0]) return;

      fichiersPhotoParSlot[slotKey] = input.files[0];
    }, true);

    document.addEventListener('click', function (e) {
      var bouton = e && e.target && e.target.closest
        ? e.target.closest('[data-clear-image-slot]')
        : null;
      if (!bouton) return;

      var slotKey = String(bouton.getAttribute('data-clear-image-slot') || '').trim();
      if (slotKey) delete fichiersPhotoParSlot[slotKey];
    }, true);
  }

  function fichierPhotoCourante(actions) {
    var input = actions && actions.querySelector ? actions.querySelector('[data-image-slot]') : null;
    if (input && input.files && input.files[0]) return input.files[0];

    var slotKey = input && input.getAttribute
      ? String(input.getAttribute('data-image-slot') || '').trim()
      : '';
    return (slotKey && fichiersPhotoParSlot[slotKey]) || null;
  }

  /* La photo à transformer est celle déjà posée dans l'emplacement : on la
     relit depuis l'aperçu du panneau plutôt que de redemander un fichier. */
  function photoCourante(actions) {
    var carte = actions.closest ? actions.closest('.oz-smarttpl-image-slot') : null;
    var img = carte && carte.querySelector('.oz-smarttpl-inline-preview img');
    return (img && img.getAttribute('src')) || '';
  }

  /* Le serveur attend un multipart/form-data avec le fichier sous la clé
     "image", exactement comme la fiche produit l'envoie. Un POST JSON avec
     une URL, ce que je faisais, lui est incompréhensible : d'où l'échec
     malgré une photo parfaitement nette. */
  function absolutiser(url, base) {
    try { return new URL(url, base).href; } catch (e) { return url; }
  }

  function estEndpointFlux(url) {
    return /\/(?:v2\/ai-[a-z0-9-]+|ai-family-illustration)\/generate\.php(?:\?|$)/i.test(String(url || ''));
  }

  function nomPhotoPourBlob(blob) {
    var type = String((blob && blob.type) || '').toLowerCase();
    if (type.indexOf('jpeg') !== -1 || type.indexOf('jpg') !== -1) return 'photo.jpg';
    if (type.indexOf('webp') !== -1) return 'photo.webp';
    return 'photo.png';
  }

  function dataUriVersBlob(uri) {
    var match = String(uri || '').match(/^data:([^;,]*)(;base64)?,([\s\S]*)$/i);
    if (!match) throw new Error('L’aperçu de la photo est invalide.');

    var type = match[1] || 'application/octet-stream';
    var contenu;

    try {
      contenu = match[2]
        ? window.atob(match[3].replace(/\s/g, ''))
        : decodeURIComponent(match[3]);
    } catch (e) {
      throw new Error('L’aperçu de la photo ne peut pas être décodé.');
    }

    var octets = new Uint8Array(contenu.length);
    for (var i = 0; i < contenu.length; i++) octets[i] = contenu.charCodeAt(i) & 255;
    return new Blob([octets], { type: type });
  }

  function lireJsonTransformation(res, cfg) {
    return res.text().then(function (txt) {
      var propre = String(txt || '').replace(/^\uFEFF/, '').trim();
      var json = null;

      try {
        json = propre ? JSON.parse(propre) : null;
      } catch (e) {
        console.error('[oazar-ai] réponse non JSON :', propre.slice(0, 300));
        throw new Error('Réponse serveur illisible (HTTP ' + res.status + ').');
      }

      if (!res.ok) {
        throw new Error((json && json.error) || ('Erreur serveur ' + res.status + '.'));
      }
      if (!json || json.success !== true) {
        throw new Error((json && json.error) || cfg.error);
      }

      return json;
    });
  }

  function lireFluxTransformation(res, cfg) {
    if (!res.body || typeof res.body.getReader !== 'function' || !window.TextDecoder) {
      return lireJsonTransformation(res, cfg);
    }

    var reader = res.body.getReader();
    var decoder = new TextDecoder('utf-8');
    var buffer = '';
    var resultat = null;

    function traiterBloc(bloc) {
      bloc = String(bloc || '').trim();
      if (!bloc || bloc.charAt(0) === ':') return;

      var evenement = '';
      var donnees = [];

      bloc.split('\n').forEach(function (ligne) {
        if (ligne.indexOf('event:') === 0) evenement = ligne.slice(6).trim();
        if (ligne.indexOf('data:') === 0) donnees.push(ligne.slice(5).trim());
      });

      if (!donnees.length) return;

      var payload;
      try {
        payload = JSON.parse(donnees.join('\n'));
      } catch (e) {
        throw new Error('Le flux du serveur IA est illisible.');
      }

      if (evenement === 'error' || payload.success === false) {
        throw new Error(payload.error || cfg.error);
      }

      if (evenement === 'done' && payload.image_url) {
        resultat = payload;
      }
    }

    function viderBlocs() {
      buffer = buffer.replace(/\r\n/g, '\n');
      var position;

      while ((position = buffer.indexOf('\n\n')) !== -1) {
        var bloc = buffer.slice(0, position);
        buffer = buffer.slice(position + 2);
        traiterBloc(bloc);
      }
    }

    function lire() {
      return reader.read().then(function (partie) {
        if (partie.done) {
          buffer += decoder.decode();
          viderBlocs();
          if (buffer.trim()) traiterBloc(buffer);
          if (!resultat) throw new Error('La connexion s’est fermée avant de recevoir l’illustration.');
          return resultat;
        }

        buffer += decoder.decode(partie.value, { stream: true });
        viderBlocs();
        return lire();
      });
    }

    return lire();
  }

  /* Envoi direct. L'illustration famille utilise un flux SSE afin que
     LiteSpeed voie la connexion active pendant toute la génération. */
  function envoyerPhoto(url, cfg, slotKey, blob, signal) {
    var flux = estEndpointFlux(url);
    var form = new FormData();
    form.append('image', blob, nomPhotoPourBlob(blob));
    form.append('mode', cfg.mode);
    form.append('preset', cfg.preset);
    form.append('slot_id', String(slotKey || 'photo'));
    form.append('fields_json', '{}');
    if (flux) form.append('stream', '1');

    return fetch(url, {
      method: 'POST',
      body: form,
      headers: { Accept: flux ? 'text/event-stream, application/json' : 'application/json' },
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      signal: signal
    }).then(function (res) {
      var type = String(res.headers.get('content-type') || '').toLowerCase();
      if (!res.ok || type.indexOf('text/event-stream') === -1) {
        return lireJsonTransformation(res, cfg);
      }
      return lireFluxTransformation(res, cfg);
    });
  }

  function transformerPhoto(cfg, slotKey, source, surProgres) {
    var controller = ('AbortController' in window) ? new AbortController() : null;
    var depart = Date.now();
    var expire = false;

    var minuteur = window.setTimeout(function () {
      expire = true;
      if (controller) { try { controller.abort(); } catch (e) {} }
    }, cfg.timeoutMs);

    /* Une generation en qualite haute peut demander plusieurs minutes. Sans
       retour visible, on ne distingue pas une attente normale d'un blocage. */
    var tic = window.setInterval(function () {
      if (typeof surProgres === 'function') {
        surProgres(Math.round((Date.now() - depart) / 1000));
      }
    }, 1000);

    var lirePhoto;
    if (typeof Blob !== 'undefined' && source instanceof Blob) {
      lirePhoto = Promise.resolve(source);
    } else if (/^data:/i.test(String(source || ''))) {
      /* Pas de fetch(data:). Certains navigateurs appliquent connect-src aux
         URL data alors que l'aperçu <img> reste autorise. */
      try {
        lirePhoto = Promise.resolve(dataUriVersBlob(source));
      } catch (e) {
        lirePhoto = Promise.reject(e);
      }
    } else {
      lirePhoto = fetch(source, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
        .then(function (r) {
          if (!r.ok) throw new Error('La photo importée ne peut pas être relue (HTTP ' + r.status + ').');
          return r.blob();
        })
        .catch(function (err) {
          var message = (err && err.message) ? String(err.message) : '';
          if (/^La photo importée/i.test(message)) throw err;
          throw new Error('La photo importée ne peut pas être relue par le navigateur. Réimportez-la puis réessayez.');
        });
    }

    return lirePhoto
      .then(function (blob) {
        var signal = controller ? controller.signal : undefined;

        return envoyerPhoto(cfg.endpoint, cfg, slotKey, blob, signal);
      })
      .then(function (payload) {
        var brut = String(payload.image_url || '').trim();
        if (!brut) throw new Error('Le serveur IA n\'a renvoyé aucune image.');

        return { url: absolutiser(brut, cfg.endpoint), name: 'illustration.png' };
      })
      .catch(function (err) {
        /* « signal is aborted without reason » est le message brut du
           navigateur quand AbortController.abort() est appele sans motif.
           Il n'apprend rien : on le remplace par le vrai diagnostic. */
        if (cfg.mode === 'family_source_lock' && typeof window.oazarCreateLocalIllustration === 'function') {
          return lirePhoto.then(function (blob) {
            return window.oazarCreateLocalIllustration(blob);
          });
        }
        if (expire || (err && err.name === 'AbortError')) {
          throw new Error(
            'Le serveur n’a pas répondu en ' +
            Math.round(cfg.timeoutMs / 1000) +
            ' s. Vérifiez health.php et le journal d’erreur du dossier Famille.'
          );
        }
        throw err;
      })
      .finally(function () {
        window.clearTimeout(minuteur);
        window.clearInterval(tic);
      });
  }

  function decorateSlotButtons(scope) {
    var root = scope || document;
    root.querySelectorAll('.oz-smarttpl-image-actions').forEach(function (actions) {
      if (actions.querySelector('[data-ai-slot-btn]')) return;
      var input = actions.querySelector('[data-image-slot]');
      if (!input) return;
      var slotKey = input.getAttribute('data-image-slot');
      var cfg = lireConfigSlot(actions);

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'oz-smarttpl-secondary-btn oz-smarttpl-ai-btn';
      btn.setAttribute('data-ai-slot-btn', slotKey);
      var note = document.createElement('p');
      note.className = 'oz-ai-slot-quota';
      note.textContent = '3 générations par jour pour cette IA, par connexion. Renouvellement à minuit, heure de Paris.';
      var noteId = 'oz-ai-quota-' + Math.random().toString(36).slice(2,10);
      note.id = noteId;
      btn.setAttribute('aria-describedby', noteId);
      actions.appendChild(note);

      if (!cfg) {
        /* Emplacement ordinaire : génération à partir d'un texte. */
        btn.textContent = "G\u00e9n\u00e9rer avec l'IA";
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          openAi({ slotTarget: slotKey });
        });
        actions.insertBefore(btn, note);
        return;
      }

      btn.textContent = cfg.label;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var apercu = photoCourante(actions);
        if (!apercu) {
          notify("Importez d'abord une photo dans cet emplacement.", 'warning');
          return;
        }

        /* Priorite au fichier original. On n'utilise l'URL d'apercu qu'en
           secours pour les personnalisations deja chargees. */
        var source = fichierPhotoCourante(actions) || apercu;

        var libelle = btn.textContent;
        btn.disabled = true;
        btn.textContent = cfg.loading;

        var renduLocal = false;
        transformerPhoto(cfg, slotKey, source, function (secondes) {
          btn.textContent = cfg.loading + ' ' + secondes + ' s';
        })
          .then(function (item) {
            renduLocal = !!item.local;
            return fillSlot(slotKey, item);
          })
          .then(function (ok) {
            if (ok) notify(renduLocal ? 'Aperçu stylisé créé sur votre appareil.' : 'Photo transform\u00e9e.', 'success');
          })
          .catch(function (err) {
            /* Le serveur renvoie des messages precis — photo trop petite,
               format refuse, erreur OpenAI. Les remplacer par un texte
               generique obligeait a ouvrir la console pour comprendre. */
            console.error('[oazar-ai] transformation impossible', err);

            var message = (err && err.message) ? String(err.message).trim() : '';
            var generique = !message ||
              /^(Failed to fetch|NetworkError|AbortError)/i.test(message);

            notify(generique ? cfg.error : message, 'error');
          })
          .finally(function () {
            btn.disabled = false;
            btn.textContent = libelle;
          });
      });

      actions.insertBefore(btn, note);
    });
  }

  function observeSlots() {
    var mo = new MutationObserver(function (records) {
      var touched = false;
      records.forEach(function (r) {
        if (r.addedNodes && r.addedNodes.length) touched = true;
      });
      if (touched) decorateSlotButtons(document);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    decorateSlotButtons(document);
  }

  /* ---------------------------------------------------------------------- */
  /* Paramètres d'URL : ?ai_prompt= et ?template=                           */
  /* ---------------------------------------------------------------------- */

  function resolveTemplateId(keyOrId) {
    var api = window.OazarSmartTemplates;
    if (!api || typeof api.getCatalog !== 'function') return null;
    var needle = String(keyOrId || '').trim().toLowerCase();
    if (!needle) return null;
    var catalog;
    try { catalog = api.getCatalog(); } catch (e) { return null; }
    var list = (catalog && catalog.templates) || [];
    for (var i = 0; i < list.length; i++) {
      var t = list[i];
      if (String(t.id || '').toLowerCase() === needle) return t.id;
      if (String(t.key || '').toLowerCase() === needle) return t.id;
    }
    return null;
  }

  function handleUrlParams() {
    var params;
    try { params = new URLSearchParams(window.location.search || ''); } catch (e) { return; }

    var tpl = params.get('template') || params.get('smart_template');
    if (tpl) {
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        var id = resolveTemplateId(tpl);
        if (id && window.OazarSmartTemplates && typeof window.OazarSmartTemplates.openTemplateById === 'function') {
          clearInterval(timer);
          try { window.OazarSmartTemplates.openTemplateById(id, { autoOpened: true }); } catch (e) {}
        } else if (tries > 40) {
          clearInterval(timer);
          console.warn('[oazar-ai] modèle introuvable :', tpl);
        }
      }, 250);
    }

    var p = params.get('ai_prompt');
    if (p) {
      setTimeout(function () {
        openAi({ prompt: decodeURIComponent(p), style: params.get('ai_style') || uiState.style });
      }, 900);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Démarrage                                                              */
  /* ---------------------------------------------------------------------- */

  function boot() {
    if (CONFIG.enabled === false || CONFIG.enabled === 'false') return;

    // La section charge customizer-ai.css avant ce script ; garder la feuille
    // unique évite qu'un style violet embarqué écrase le thème de l'atelier.
    injectDesktopCard();
    injectMobileButton();
    activerMemoirePhoto();
    observeSlots();

    handleUrlParams();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* API publique */
  window.OazarAI = {
    config: CONFIG,
    styles: STYLES,
    open: openAi,
    generate: generate,
    addToProduct: addToProduct,
    fillSlot: fillSlot,
    history: historyRead,
    quotaLeft: quotaLeft,
    renderInto: renderInto
  };
})();
